import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Check, ChevronRight, ChevronLeft, Building2, Palette, LifeBuoy, Mail, Globe,
  PartyPopper, AlertCircle, Sparkles, ArrowRight, FileText, Settings as SettingsIcon, Eye,
} from "lucide-react";
import EmailTestCard from "@/components/EmailTestCard";

/**
 * Phase 4.3 — Mandanten-Onboarding (Solarfirma).
 * Route: /onboarding/client
 *
 * Scope: NUR Endkunden-CI + Angebotssetup.
 * KEIN PV-Konfigurator, KEINE Lead-Generierung, KEIN Agenturportal.
 */

const STEPS = [
  { n: 1, label: "Firmenprofil", icon: Building2 },
  { n: 2, label: "Branding", icon: Palette },
  { n: 3, label: "Support & Recht", icon: LifeBuoy },
  { n: 4, label: "E-Mail-CI", icon: Mail },
  { n: 5, label: "Angebotsdomain", icon: Globe },
];

const DOMAIN_STATUS_INFO = {
  not_configured: { label: "Nicht konfiguriert", color: "bg-slate-200 text-slate-700" },
  pending_dns: { label: "DNS ausstehend", color: "bg-amber-100 text-amber-800" },
  verified: { label: "Verifiziert", color: "bg-emerald-100 text-emerald-800" },
  active: { label: "Aktiv", color: "bg-emerald-600 text-white" },
  error: { label: "Fehler", color: "bg-red-100 text-red-800" },
};

export default function ClientOnboarding() {
  const { user, company, checkAuth } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [dnsChecking, setDnsChecking] = useState(false);
  const [dnsDetail, setDnsDetail] = useState("");

  const isClientUser = user?.role === "client_user";

  const [profile, setProfile] = useState({
    name: "", contact_name: "", email: "", phone: "", website: "", address: "",
  });
  const [branding, setBranding] = useState({ logo_url: "", primary_color: "#f97316", accent_color: "#0f172a" });
  const [support, setSupport] = useState({ support_email: "", support_phone: "", imprint_url: "", privacy_url: "" });
  const [emailCi, setEmailCi] = useState({ email_from_name: "", email_from_address: "", email_reply_to: "" });
  const [domain, setDomain] = useState("");

  // Initiale Werte aus company übernehmen (deferred — vermeidet R19 set-state-in-effect)
  useEffect(() => {
    if (!company) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setProfile({
        name: company.name || "",
        contact_name: company.contact_name || "",
        email: company.email || "",
        phone: company.phone || "",
        website: company.website || "",
        address: company.address || "",
      });
      setBranding({
        logo_url: company.logo_url || "",
        primary_color: company.primary_color || "#f97316",
        accent_color: company.accent_color || "#0f172a",
      });
      setSupport({
        support_email: company.support_email || "",
        support_phone: company.support_phone || "",
        imprint_url: company.imprint_url || "",
        privacy_url: company.privacy_url || "",
      });
      setEmailCi({
        email_from_name: company.email_from_name || "",
        email_from_address: company.email_from_address || "",
        email_reply_to: company.email_reply_to || "",
      });
      setDomain(company.custom_offer_domain || "");
      // Wenn schon ein Step gespeichert ist → dort starten
      if (company.onboarding_step && !company.onboarding_completed) {
        setStep(Math.max(1, Math.min(5, company.onboarding_step)));
      }
    });
    return () => { cancelled = true; };
  }, [company]);

  const patchSettings = async (data, nextStep) => {
    setSaving(true);
    try {
      const payload = { ...data };
      if (nextStep) payload.onboarding_step = nextStep;
      await api.patch("/settings", payload);
      await checkAuth();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
      return false;
    } finally { setSaving(false); }
  };

  const onSaveStep1 = async () => {
    if (!profile.name.trim()) { toast.error("Firmenname fehlt"); return; }
    if (await patchSettings(profile, 2)) setStep(2);
  };
  const onSaveStep2 = async () => {
    if (await patchSettings(branding, 3)) setStep(3);
  };
  const onSaveStep3 = async () => {
    if (!support.imprint_url.trim() || !support.privacy_url.trim()) {
      toast.warning("Hinweis: Impressum/Datenschutz fehlen — Sie können das auch später ergänzen.");
    }
    if (await patchSettings(support, 4)) setStep(4);
  };
  const onSaveStep4 = async () => {
    if (await patchSettings(emailCi, 5)) setStep(5);
  };
  const onCheckDns = async () => {
    if (!domain.trim()) { toast.error("Bitte zuerst eine Domain eintragen"); return; }
    // Domain speichern
    await patchSettings({ custom_offer_domain: domain.trim() }, 5);
    setDnsChecking(true);
    setDnsDetail("");
    try {
      const r = await api.post("/settings/custom-domain/check");
      setDnsDetail(r.data.detail || "");
      if (r.data.status === "active") toast.success("Domain ist aktiv!");
      else toast.message(`Status: ${r.data.status}`);
      await checkAuth();
    } catch (e) {
      toast.error("DNS-Prüfung fehlgeschlagen");
    } finally { setDnsChecking(false); }
  };
  const onFinish = async () => {
    // Letzten Stand der Domain mitspeichern (falls eingetragen)
    if (domain !== (company?.custom_offer_domain || "")) {
      await patchSettings({ custom_offer_domain: domain.trim() }, 5);
    }
    setSaving(true);
    try {
      await api.post("/company/onboarding/complete");
      await checkAuth();
      setDone(true);
    } catch (e) {
      toast.error("Abschluss fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (isClientUser) {
    return (
      <Card className="p-8 border-amber-200 bg-amber-50 max-w-2xl mx-auto" data-testid="client-onboarding-forbidden">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
          <div>
            <h2 className="font-bold text-slate-900 text-lg">Keine Berechtigung</h2>
            <p className="text-sm text-slate-700 mt-1">Das Mandanten-Onboarding kann nur von Mandanten-Admins oder Agentur-Admins eingerichtet werden. Bitte kontaktieren Sie Ihren Admin.</p>
          </div>
        </div>
      </Card>
    );
  }

  if (done) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="client-onboarding-done">
        <Card className="p-10 space-y-5 border-emerald-200 bg-emerald-50 text-center">
          <PartyPopper className="w-12 h-12 text-emerald-600 mx-auto" />
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Ihr Angebotsauftritt ist eingerichtet.
          </h2>
          <p className="text-slate-700">Ab jetzt erscheinen Logo, Farben und Footer-Daten auf allen Angebotsseiten und E-Mails an Ihre Endkunden.</p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Button data-testid="finish-go-dashboard" onClick={() => nav("/dashboard")} className="bg-orange-600 hover:bg-orange-700 text-white">
              Zum Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button data-testid="finish-create-lead" variant="outline" onClick={() => nav("/leads/new")}>
              <FileText className="w-4 h-4 mr-2" /> Ersten Lead anlegen
            </Button>
            <Button data-testid="finish-open-settings" variant="outline" onClick={() => nav("/settings")}>
              <SettingsIcon className="w-4 h-4 mr-2" /> Einstellungen
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="client-onboarding-page">
      <header>
        <Badge className="bg-orange-50 text-orange-700 hover:bg-orange-50 border border-orange-200 text-[10px] uppercase tracking-widest mb-2">
          Mandanten-Onboarding · für Ihre Endkunden
        </Badge>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Angebotsauftritt einrichten
        </h1>
        <p className="mt-1 text-slate-600">Sie richten gleich Ihr Branding, Ihre Supportdaten und Ihre E-Mail-Absenderdaten ein. <b>Das sehen Ihre Solar-Endkunden</b> auf Angebotsseiten und in der Kommunikation.</p>
      </header>

      <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="client-onboarding-stepper">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          const active = step === s.n;
          const finished = step > s.n;
          return (
            <React.Fragment key={s.n}>
              <div
                data-testid={`client-step-${s.n}-indicator`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  active ? "bg-orange-600 text-white" : finished ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                }`}
              >
                {finished ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                <span className="text-sm font-medium">{s.label}</span>
              </div>
              {idx < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="client-step-1-card">
          <h2 className="text-lg font-bold">1 — Firmenprofil</h2>
          <p className="text-sm text-slate-500 -mt-3">Diese Daten werden für Ihr Angebotsportal und Ihre Kundenkommunikation verwendet.</p>
          <Field label="Firmenname *"><Input data-testid="co-name" value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="Solar Müller GmbH" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ansprechpartner"><Input data-testid="co-contact" value={profile.contact_name} onChange={e => setProfile(p => ({ ...p, contact_name: e.target.value }))} /></Field>
            <Field label="E-Mail"><Input data-testid="co-email" type="email" value={profile.email} onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} placeholder="vertrieb@…" /></Field>
            <Field label="Telefon"><Input data-testid="co-phone" value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+49 …" /></Field>
            <Field label="Website"><Input data-testid="co-website" value={profile.website} onChange={e => setProfile(p => ({ ...p, website: e.target.value }))} placeholder="https://…" /></Field>
            <div className="col-span-2">
              <Field label="Adresse"><Input data-testid="co-address" value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} placeholder="Straße, PLZ Ort" /></Field>
            </div>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="client-step-2-card">
          <h2 className="text-lg font-bold">2 — Branding für Endkunden</h2>
          <p className="text-sm text-slate-500 -mt-3">Dieses Branding sehen Ihre Endkunden auf Angebotsseiten und später in E-Mails.</p>
          <Field label="Logo-URL">
            <div className="flex gap-3 items-center">
              <Input data-testid="co-logo-url" value={branding.logo_url} onChange={e => setBranding(b => ({ ...b, logo_url: e.target.value }))} placeholder="https://…/logo.png" className="flex-1" />
              {branding.logo_url && <img src={branding.logo_url} alt="" className="h-10 max-w-[120px] object-contain bg-white rounded border p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primärfarbe">
              <div className="flex gap-2 items-center">
                <input type="color" value={branding.primary_color} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))} className="h-10 w-12 rounded border cursor-pointer" />
                <Input data-testid="co-primary-color" value={branding.primary_color} onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))} className="font-mono flex-1" />
              </div>
            </Field>
            <Field label="Akzentfarbe">
              <div className="flex gap-2 items-center">
                <input type="color" value={branding.accent_color} onChange={e => setBranding(b => ({ ...b, accent_color: e.target.value }))} className="h-10 w-12 rounded border cursor-pointer" />
                <Input data-testid="co-accent-color" value={branding.accent_color} onChange={e => setBranding(b => ({ ...b, accent_color: e.target.value }))} className="font-mono flex-1" />
              </div>
            </Field>
          </div>
          {/* Live-Vorschau */}
          <div className="rounded-lg p-4 border" style={{ borderColor: `${branding.primary_color}33`, background: `linear-gradient(135deg, ${branding.primary_color}10, ${branding.accent_color}05)` }} data-testid="co-brand-preview">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">So sieht es Ihr Endkunde</span>
            </div>
            <div className="bg-white rounded-md p-4 flex items-center gap-3">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="" className="h-10 max-w-[100px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: branding.primary_color }}>
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1">
                <p className="font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{profile.name || "Ihr Firmenname"}</p>
                <p className="text-xs text-slate-500">Ihr persönliches PV-Angebot</p>
              </div>
              <Button size="sm" className="text-white" style={{ background: branding.primary_color }}>Angebot ansehen</Button>
            </div>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="client-step-3-card">
          <h2 className="text-lg font-bold">3 — Support &amp; Rechtliches</h2>
          <p className="text-sm text-slate-500 -mt-3">Diese Informationen werden im Footer Ihrer Angebotsseiten angezeigt.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Support-E-Mail"><Input data-testid="co-support-email" value={support.support_email} onChange={e => setSupport(s => ({ ...s, support_email: e.target.value }))} placeholder="support@…" /></Field>
            <Field label="Support-Telefon"><Input data-testid="co-support-phone" value={support.support_phone} onChange={e => setSupport(s => ({ ...s, support_phone: e.target.value }))} placeholder="+49 …" /></Field>
            <Field label="Impressum-URL"><Input data-testid="co-imprint" value={support.imprint_url} onChange={e => setSupport(s => ({ ...s, imprint_url: e.target.value }))} placeholder="https://…/impressum" /></Field>
            <Field label="Datenschutz-URL"><Input data-testid="co-privacy" value={support.privacy_url} onChange={e => setSupport(s => ({ ...s, privacy_url: e.target.value }))} placeholder="https://…/datenschutz" /></Field>
          </div>
          {(!support.imprint_url || !support.privacy_url) && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="co-legal-warning">
              Hinweis: In Deutschland ist ein Impressum &amp; Datenschutz auch auf Angebotsseiten empfohlen. Sie können das auch später ergänzen.
            </p>
          )}
        </Card>
      )}

      {step === 4 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="client-step-4-card">
          <h2 className="text-lg font-bold">4 — E-Mail-CI</h2>
          <p className="text-sm text-slate-500 -mt-3">Reply-To und Absenderanzeige werden für die spätere E-Mail-Kommunikation gespeichert. <b>In dieser Phase wird der Versand technisch noch vorbereitet/simuliert.</b></p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Absender-Name (From)"><Input data-testid="co-email-from-name" value={emailCi.email_from_name} onChange={e => setEmailCi(s => ({ ...s, email_from_name: e.target.value }))} placeholder={profile.name || "Solar Müller GmbH"} /></Field>
            <Field label="Antwort-Adresse (Reply-To)"><Input data-testid="co-email-reply-to" value={emailCi.email_reply_to} onChange={e => setEmailCi(s => ({ ...s, email_reply_to: e.target.value }))} placeholder="vertrieb@…" /></Field>
            <div className="col-span-2">
              <Field label="Gewünschte Absender-Adresse">
                <div className="flex items-center gap-3">
                  <Input data-testid="co-email-from-address" value={emailCi.email_from_address} onChange={e => setEmailCi(s => ({ ...s, email_from_address: e.target.value }))} placeholder="angebote@ihrebetrieb.de" className="flex-1" />
                  <Badge variant="outline" className="text-xs whitespace-nowrap" data-testid="co-email-sender-status">
                    {company?.email_sender_status === "verified" ? "Domain verifiziert" : "Reply-To-Modus"}
                  </Badge>
                </div>
              </Field>
            </div>
          </div>
          {/* Phase 5 — Direkt-Test mit den gerade eingegebenen From/Reply-To-Werten.
              Wichtig: Test feuert mit den AKTUELL gespeicherten Werten, daher
              Hinweis: vorher „Weiter" klicken oder Reply-To/From speichern. */}
          <div className="border-t border-slate-200 pt-4 mt-2" data-testid="co-step4-test-wrapper">
            <p className="text-xs text-slate-500 mb-2">Speichern Sie zuerst Ihre E-Mail-CI mit &bdquo;Weiter&ldquo; — dann können Sie hier eine Test-E-Mail an Ihre eigene Adresse senden, um zu sehen, wie sie ankommt.</p>
            <EmailTestCard variant="tenant" />
          </div>
        </Card>
      )}

      {step === 5 && (
        <Card className="p-6 space-y-4 border-slate-200" data-testid="client-step-5-card">
          <h2 className="text-lg font-bold">5 — Angebotsdomain (optional)</h2>
          <p className="text-sm text-slate-500 -mt-3">Diese Domain wird für <b>Angebotsseiten und Endkundenkommunikation</b> verwendet. Nicht zu verwechseln mit der Agenturportal-Domain.</p>
          <Field label="Custom-Angebotsdomain">
            <Input
              data-testid="co-offer-domain"
              value={domain}
              onChange={e => setDomain(e.target.value.toLowerCase())}
              placeholder="angebot.solar-mueller.de"
              className="font-mono"
            />
          </Field>
          {domain.trim() && (
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-2 border border-slate-200" data-testid="co-dns-block">
              <p className="font-semibold text-slate-700 text-xs uppercase tracking-wider">DNS-Anweisung</p>
              <p className="text-slate-600 text-xs">Setzen Sie bei Ihrem DNS-Provider folgenden <span className="font-mono font-semibold">CNAME</span>-Eintrag:</p>
              <pre className="bg-white border border-slate-200 rounded px-3 py-2 text-xs overflow-x-auto">
                <span className="text-slate-500">CNAME</span>  {domain.trim()}  →  {company?.custom_offer_domain_dns_target || "cname.revenioo.ai"}
              </pre>
              <div className="flex items-center justify-between gap-3 pt-1">
                <Badge className={(DOMAIN_STATUS_INFO[company?.custom_offer_domain_status] || DOMAIN_STATUS_INFO.not_configured).color}>
                  {(DOMAIN_STATUS_INFO[company?.custom_offer_domain_status] || DOMAIN_STATUS_INFO.not_configured).label}
                </Badge>
                <Button data-testid="co-check-dns-btn" onClick={onCheckDns} disabled={dnsChecking} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
                  {dnsChecking ? "Prüfe …" : "DNS prüfen"}
                </Button>
              </div>
              {dnsDetail && <p className="text-xs text-slate-600" data-testid="co-dns-detail">{dnsDetail}</p>}
            </div>
          )}
          {!domain.trim() && (
            <p className="text-xs text-slate-400">Optional — Sie können das auch jederzeit später in den Einstellungen ergänzen.</p>
          )}
        </Card>
      )}

      <div className="flex justify-between items-center" data-testid="co-nav">
        <Button data-testid="co-back-btn" variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1 || saving}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>
        <div className="flex items-center gap-2">
          {step < 5 && (
            <Button data-testid="co-skip-btn" variant="ghost" onClick={() => setStep(step + 1)} disabled={saving} className="text-slate-500">
              Überspringen
            </Button>
          )}
          <Button
            data-testid="co-next-btn"
            onClick={
              step === 1 ? onSaveStep1
              : step === 2 ? onSaveStep2
              : step === 3 ? onSaveStep3
              : step === 4 ? onSaveStep4
              : onFinish
            }
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {saving ? "Speichern …" : step === 5 ? "Setup abschließen" : (<>Weiter <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
  </div>
);
